import { FieldType } from 'modules/domains';
import { List as ListData } from 'modules/domains/list';

export type SearchGroupIndex = number;
export type SearchQueryIndex = number;
export type CriteriaPropertyIndex = number;
export type DynamicValue = string | null | undefined;

export type FieldOption = {
  name: string;
  value: string;
  type: FieldType;
};

export enum QueryBuilderIndex {
  UserAndEvent = 'UserAndEvent',
  Catalog = 'Catalog',
}

export type QueryBuilderV1Props = {
  /** Whether user has RBAC permission to manage catalogs */
  canManageCatalogs: boolean;
  children?: React.ReactNode;
  cloningValidationMessages?: Array<ValidationMessage>;
  /** Value of the query combinator. */
  combinator: string;
  /** Append text of top level combinator. */
  combinatorAppendText?: string;
  /** Prepend text of top level combinator. */
  combinatorPrependText?: string;
  /** Additional props for the comparator dropdown. Mainly used for passing in custom comparator types. */
  comparatorDropdownProps: any;
  /** The value of an empty search group. */
  emptySearchGroup?: any;
  /** The value of an empty search query. */
  emptySearchQuery?: any;
  /** The value of an empty criteria property . */
  emptyCriteriaProperty?: any;
  /**
   * Fetches the user mappings for dynamic queries.
   *
   * @returns { void }
   */
  fetchUserMappings: () => void;
  filteredFieldsByPropertyMap?: any;
  /**
   * Callback function that returns the selected property to get fields for if fields do not exist.
   *
   * @param { String } property: The property to get fields for.
   * @returns { void }
   */
  getFieldsForProperty?: (property: string) => void;
  /** Array of properties to hide. */
  hiddenProperties?: Array<string>;
  /** Array of properties to disable. */
  disabledProperties?: Array<string>;
  /** Append text of a group combinator. */
  groupCombinatorAppendText?: string;
  /** Prepend text of a group combinator. */
  groupCombinatorPrependText?: string;
  /** if true, some fields or values couldn't be copied over from a clone. Used to display warning banner */
  hasCloningErrors?: boolean;
  /** Hides grouping if only one search query group exists. */
  hideGroupsOnSingle?: boolean;
  /** Hides the property dropdown. */
  hideProperty?: boolean;
  /**
   * Initializes the query builder with props and initial search query.
   *
   * @param { Object } searchGroup: The search group to add.
   * @param { Object } queryBuilderProps: Props for the query builder.
   * @returns { void }
   */
  initializeQuery?: (searchGroup: any, queryBuilderProps: any) => any;
  /** If product recommendations feature flag is on. */
  isProductRecommendationsOn?: boolean;
  /** If user context finished fetching. */
  isUserContextLoaded?: boolean;
  /** If query builder is currently loading. */
  isLoading: boolean;
  /** If query builder is in read only mode */
  isReadOnly?: boolean;
  /** If query builder is in view mode. */
  isViewMode?: boolean;
  /** Additional content to show in the limit and order by row. */
  limitAndOrderByRowContent?: React.ReactNode;
  /** Message to display when loading. */
  loadingMessage?: string;
  /**
   * Callback function when adding a search group.
   *
   * @param { Object } searchGroup: The search group to add.
   * @returns { void }
   */

  onAddSearchGroup?: (searchGroup: any) => any;
  /**
   * Callback function when changing the combinator.
   *
   * @param { String } combinator: The new combinator value.
   * @returns { void }
   */
  onChangeCombinator?: (value: string) => any;
  /**
   * Moves a search query.
   *
   * @param { Number } searchQueryIndex: The index of the search query to move.
   * @param { String } searchGroupId: The ID of the group to move the search query from.
   * @param { Number } newSearchQueryIndex: The new index to move the search query to.
   * @param { String } newSearchGroupId: The ID of the search group to move the search query to.
   * @returns { void }
   */
  onSearchQueryMove?: (
    searchQueryIndex: number,
    searchGroupId: string,
    newSearchQueryIndex: number,
    newSearchGroupId: string,
  ) => any;
  /**
   * Reorders a search query.
   *
   * @param { Number } searchQueryIndex: The index of the search query to reorder.
   * @param { Number } newSearchQueryIndex: The new index of the search query.
   * @param { String } searchGroupId: The ID of the search group to reorder the search query from.
   * @returns { Object } action.
   */
  onSearchQueryReorder?: (
    searchQueryIndex: number,
    newSearchQueryIndex: number,
    searchGroupId: string,
  ) => any;
  /** The orderBy object. */
  orderBy?: number;
  properties?: {
    text: string;
    value?: string;
    section?: boolean;
  }[]; // TypeScript equivalent defined as Property[], see here: src/modules/queryBuilder/legacyQueryBuilder/components/propertyDropdown/types.ts
  /** Placeholder for property dropdown. */
  propertyPlaceholder?: string;
  /** Function to reset the preview email on PreviewResultsBar. */
  resetPreviewIdentifier: () => void;
  /** List of search groups. searchQueries value of ES Query. */
  searchGroups?: [];
  /** Show field text input for field. */
  showFieldTextInput?: boolean;
  /** Show custom date text input in relative date. */
  showCustomDateTextInput?: boolean;
  /** Show the dynamic field toggle. */
  showDynamicFieldToggle?: boolean;
  /** Show state buttons in the querybuilder */
  showStateButtons?: boolean;
  /** Show the limit and orderBy row. */
  showLimitAndOrderBy?: boolean;
  /** The size limit value. */
  sizeLimit?: number;
  /** The max size limit value. */
  sizeLimitMax?: number;
  /** Store object to get state. (Used for connected SearchGroup component) */
  store: any;
  /** The query type/indentifier. e.g. Segmentation, Catalog Collection... */
  type: string;
  /** Whether or not to display a cast type field for specific Long type criteria property fields */
  useCastTypeField?: boolean;
  /** Header to display on validation errors. */
  validationMessageHeader?: string;
  /** Which ES index is being queried against */
  esIndex?: QueryBuilderIndex;
  /** Enable validation error shown when query values contain handlebars */
  disallowHandlebarsInQuery?: boolean;
  /** Only show these properties if specified. See usage in src/modules/queryBuilder/legacyQueryBuilder/components/propertyDropdown/index.js */
  restrictToProperties?: Array<string>;
  /** Disable the max property count */
  canSetMaxPropertyCount?: boolean;
};

export type ValidationMessage = {
  validationMessageHeader: string;
  validationMessages: {
    location: string;
    items: string[];
  };
};

export type FieldFacet = {
  term: string;
  count: number;
};

export enum PhoneType {
  FIXED_LINE = 'FIXED_LINE',
  MOBILE = 'MOBILE',
  FIXED_LINE_OR_MOBILE = 'FIXED_LINE_OR_MOBILE',
  TOLL_FREE = 'TOLL_FREE',
  PREMIUM_RATE = 'PREMIUM_RATE',
  SHARED_COST = 'SHARED_COST',
  VOIP = 'VOIP',
  PERSONAL_NUMBER = 'PERSONAL_NUMBER',
  PAGER = 'PAGER',
  UAN = 'UAN',
  VOICEMAIL = 'VOICEMAIL',
  UNKNOWN = 'UNKNOWN',
}

export enum ValueType {
  Trigger = 'trigger',
  Custom = 'custom',
  Contact = 'contact',
}

export type List = Pick<ListData, 'id' | 'name'>;
